/**
 * CodeBlock component with "Try It" button and mock execution
 */

import { Play, Copy, Check } from "lucide-react";
import { useState } from "react";
import { SyntaxHighlighter } from "#app/components/assistant-ui/shiki-highlighter";
import { useMockExecution } from "#app/hooks/use-mock-execution";
import { getMockById } from "#app/lib/tutorials/mocks";
import { type MockData } from "#app/lib/tutorials/parser";
import { cn } from "#app/utils/misc";
import { ExecutionSimulator } from "./ExecutionSimulator";

export interface CodeBlockProps {
  code: string;
  language?: string;
  mockId?: string;
  mocks?: MockData[];
  className?: string;
}

export function CodeBlock({
  code,
  language = "text",
  mockId,
  mocks = [],
  className,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const mockData = mockId ? (getMockById(mocks, mockId) ?? null) : null;
  const { isExecuting, output, error, executionTime, execute, reset } =
    useMockExecution(mockData);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const handleExecute = () => {
    if (mockData) {
      void execute();
    }
  };

  return (
    <div className={cn("my-4", className)}>
      <div className="rounded-lg border border-[#DDDDDD] overflow-hidden bg-white dark:bg-zinc-900">
        {/* Code Header */}
        <div className="flex items-center justify-between bg-zinc-900 px-4 py-2 text-sm">
          <span className="font-semibold text-white lowercase">{language}</span>
          <div className="flex items-center gap-2">
            {mockData && (
              <button
                onClick={handleExecute}
                disabled={isExecuting}
                className={cn(
                  "flex items-center gap-2 px-3 py-1 rounded text-xs font-medium transition-colors",
                  "text-white bg-[linear-gradient(93.53deg,#141A42_14.58%,#6577A4_110.19%)] hover:opacity-95",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <Play className="h-3 w-3" />
                Try It
              </button>
            )}
            <button
              onClick={handleCopy}
              className="text-gray-400 hover:text-white transition-colors"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Code Content */}
        <div className="overflow-x-auto">
          <SyntaxHighlighter
            code={code}
            language={language}
            theme="github-dark"
            className="[&_pre]:m-0 [&_pre]:rounded-none"
          />
        </div>
      </div>

      {/* Execution Output */}
      <ExecutionSimulator
        isExecuting={isExecuting}
        output={output}
        error={error}
        executionTime={executionTime}
        onReset={reset}
      />
    </div>
  );
}
